import { isPlainObject } from '@vestigas/utils';

/**
 * How a DocuWare index field gets its value. The API expresses this in the
 * entry's `source`: a fixed value, one of its special sources, or a path into
 * the delivery note.
 */
export const DOCUWARE_SOURCE_KIND = {
  COMPANY_ACCOUNT_ID: 'companyAccountId',
  PATH: 'path',
  STATIC: 'static',
} as const;

export type DocuwareSourceKind =
  (typeof DOCUWARE_SOURCE_KIND)[keyof typeof DOCUWARE_SOURCE_KIND];

const SPECIAL_SOURCES: Record<string, DocuwareSourceKind> = {
  _company_account_id: DOCUWARE_SOURCE_KIND.COMPANY_ACCOUNT_ID,
  _static: DOCUWARE_SOURCE_KIND.STATIC,
};

const SOURCE_BY_KIND: Record<DocuwareSourceKind, string> = {
  companyAccountId: '_company_account_id',
  path: '',
  static: '_static',
};

/** The value types the API converts an index field to. */
const DOCUWARE_VALUE_TYPES = ['text', 'date', 'decimal', 'numeric'] as const;

export type DocuwareValueType = (typeof DOCUWARE_VALUE_TYPES)[number];

/**
 * One row of the index field mapping editor. `extras` carries every key of the
 * stored entry the editor does not show (maxLength, format, decimals,
 * description), so that editing a mapping never drops what it cannot display.
 */
export type DocuwareFieldMappingEntry = {
  /** Stable React key, not part of the API payload. */
  id: string;
  /** The index field's name in the customer's cabinet, case sensitive. */
  fieldName: string;
  sourceKind: DocuwareSourceKind;
  /** Dot path into the delivery note; only used with sourceKind `path`. */
  source: string;
  /** Fixed value; only used with sourceKind `static`. */
  value: string;
  /**
   * The stored value exactly as the API had it. The editor works in text, so
   * this keeps a number or a boolean from turning into a string on a save that
   * never touched the field.
   */
  storedValue: unknown;
  type: DocuwareValueType;
  isRequired: boolean;
  extras: Record<string, unknown>;
};

/**
 * The API stores a static value as a string, number or boolean. Anything else
 * would stringify to "[object Object]", so the row opens empty instead and the
 * form asks for the value.
 */
/**
 * The mapping the API falls back to when a contact point has none of its own,
 * offered in the form as a starting point. Kept in sync by hand with
 * DEFAULT_DOCUWARE_FIELD_MAPPING in the back-end's
 * vgsdatasubscription/contact_points/docuware_field_mapping.py, which stays the
 * source of truth: seeding it here only prefills rows the user then edits.
 */
export const DEFAULT_DOCUWARE_FIELD_MAPPING: Record<
  string,
  Record<string, unknown>
> = {
  ART_DES_DOKUMENTS: {
    max_length: 255,
    required: true,
    source: '_static',
    type: 'text',
    value: 'Lieferschein (eingehend)',
  },
  BESTELLNUMMER: {
    max_length: 255,
    required: false,
    source:
      'asset_state.body.transaction.logistics_package.0.line_item.0.agreement.buyer_order',
    type: 'text',
  },
  DATENSATZDATUM: {
    format: '%Y-%m-%d',
    required: false,
    source: 'asset_state.body.header.date',
    type: 'date',
  },
  DOCUMENTLINK: {
    max_length: 255,
    required: false,
    source: 'asset_chain.0',
    type: 'text',
  },
  EMAIL: {
    max_length: 255,
    required: false,
    source:
      'asset_state.body.transaction.agreement.buyer.legal_organization.universal_communication.email',
    type: 'text',
  },
  FIRMA: {
    max_length: 255,
    required: false,
    source:
      'asset_state.body.transaction.agreement.seller.legal_organization.name',
    type: 'text',
  },
  FIRMENKONTO: {
    max_length: 255,
    required: false,
    source:
      'asset_state.body.transaction.agreement.buyer.legal_organization.issuer_assigned_id',
    type: 'text',
  },
  FIRMENNUMMER: {
    max_length: 255,
    required: false,
    source:
      'asset_state.body.transaction.agreement.seller.legal_organization.issuer_assigned_id',
    type: 'text',
  },
  LIEFERDATUM: {
    format: '%Y-%m-%d',
    required: false,
    source: 'asset_state.body.transaction.delivery.execution.arrived',
    type: 'date',
  },
  LIEFERSCHEINNUMMER: {
    max_length: 255,
    required: true,
    source: 'asset_state.body.header.id',
    type: 'text',
  },
  // The one value that is a placeholder rather than a default: the Mandant is
  // a select-list value of the customer's own cabinet. Seeded empty so the row
  // shows as required-and-unfilled and the save stays blocked until it is set.
  MANDANT: {
    max_length: 255,
    required: true,
    source: '_static',
    type: 'text',
    value: '',
  },
  STATUS: {
    max_length: 255,
    required: false,
    source: '_static',
    type: 'text',
    value: 'Neu',
  },
};

const asStaticValue = (value: unknown) =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean'
    ? String(value)
    : '';

const asValueType = (value: unknown): DocuwareValueType =>
  DOCUWARE_VALUE_TYPES.includes(value as DocuwareValueType)
    ? (value as DocuwareValueType)
    : 'text';

/** Stored mapping -> editor rows. Unreadable entries are skipped, not guessed. */
export const toDocuwareFieldMappingEntries = (
  mapping: unknown,
): DocuwareFieldMappingEntry[] => {
  if (!isPlainObject(mapping)) {
    return [];
  }

  return Object.entries(mapping).flatMap(([fieldName, rawEntry]) => {
    if (!isPlainObject(rawEntry)) {
      return [];
    }

    const { required, source, type, value, ...extras } = rawEntry;
    const sourceText = typeof source === 'string' ? source : '';
    const sourceKind = SPECIAL_SOURCES[sourceText] ?? DOCUWARE_SOURCE_KIND.PATH;

    return [
      {
        extras,
        fieldName,
        id: crypto.randomUUID(),
        isRequired: required === true,
        source: sourceKind === DOCUWARE_SOURCE_KIND.PATH ? sourceText : '',
        sourceKind,
        storedValue: value,
        type: asValueType(type),
        value:
          sourceKind === DOCUWARE_SOURCE_KIND.STATIC
            ? asStaticValue(value)
            : '',
      },
    ];
  });
};

/**
 * Editor rows -> the stored mapping. Rows without a field name are dropped
 * rather than sent as an empty key; duplicates would collapse here, which is
 * why the form blocks submit on them.
 */
/** The edited text, or the stored value when the row was not touched. */
const staticValueOf = (entry: DocuwareFieldMappingEntry) =>
  entry.storedValue !== undefined &&
  entry.value === asStaticValue(entry.storedValue)
    ? entry.storedValue
    : entry.value;

/**
 * Editor rows -> the stored mapping. Rows without a field name are dropped
 * rather than sent as an empty key; duplicates would collapse here, which is
 * why the form blocks submit on them.
 */
export const fromDocuwareFieldMappingEntries = (
  entries: DocuwareFieldMappingEntry[],
): Record<string, Record<string, unknown>> | undefined => {
  const mapped = entries
    .filter((entry) => entry.fieldName)
    .map(
      (entry) =>
        [
          entry.fieldName,
          {
            ...entry.extras,
            required: entry.isRequired,
            source:
              entry.sourceKind === DOCUWARE_SOURCE_KIND.PATH
                ? entry.source
                : SOURCE_BY_KIND[entry.sourceKind],
            type: entry.type,
            // The API only accepts a value together with the `_static` source.
            ...(entry.sourceKind === DOCUWARE_SOURCE_KIND.STATIC
              ? { value: staticValueOf(entry) }
              : {}),
          },
        ] as const,
    );

  // Object.fromEntries defines own properties, so a field literally named
  // __proto__ becomes a key instead of replacing the object's prototype.
  // No rows means no override, which lets the API's default mapping apply.
  return mapped.length > 0 ? Object.fromEntries(mapped) : undefined;
};

/** Whether a row carries everything the API needs. */
export const isDocuwareFieldMappingEntryComplete = (
  entry: DocuwareFieldMappingEntry,
) => {
  if (!entry.fieldName.trim()) {
    return false;
  }

  if (entry.sourceKind === DOCUWARE_SOURCE_KIND.STATIC) {
    return Boolean(entry.value.trim());
  }

  return (
    entry.sourceKind === DOCUWARE_SOURCE_KIND.COMPANY_ACCOUNT_ID ||
    Boolean(entry.source.trim())
  );
};
